import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Profile } from '@ixicare/resources';
import { Alert, Button, Modal, Panel, Spinner, Table, formatDateTime } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';

interface ProfileSessionsProps {
  profileUuid: string;
  isOwnProfile: boolean;
}

const clientTypeLabel = (clientType: string): string => {
  const key = `healthcareProviders.sessions.clientType.${clientType}`;
  const label = t(key);
  // UNKNOWN covers sessions created before clients started identifying themselves,
  // and anything we could not place. Unrecognised values fall back to the raw string
  // rather than showing a missing-translation key.
  return label === key ? clientType : label;
};

const formatMoment = (value: string | null): string => formatDateTime(value) ?? '–';

/**
 * Live sessions for one profile, with a force logout.
 *
 * Holding several sessions at once is not restricted — this is here so the real
 * picture is visible before anything is limited by it. Force logout ends every
 * session and removes the push subscriptions, so the person's devices stop alerting;
 * they can log straight back in. It is how a seat is reclaimed now rather than
 * waiting out the token lifetime.
 *
 * On your own profile the same action is a "log out everywhere": it takes the current
 * tab down with it, so success ends the local session too instead of refetching a list
 * the token can no longer read.
 */
export const ProfileSessions: React.FC<ProfileSessionsProps> = ({ profileUuid, isOwnProfile }) => {
  const { permissions, auth } = useStores();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);

  const sessionsQuery = useQuery({
    queryKey: ['profileSessions', profileUuid],
    queryFn: () => Profile.getProfileSessions(profileUuid),
    enabled: !!profileUuid,
  });

  const revoke = useMutation({
    mutationFn: () => Profile.revokeProfileSessions(profileUuid),
    onSuccess: () => {
      setConfirming(false);
      if (isOwnProfile) {
        void auth.logout();
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['profileSessions', profileUuid] });
    },
  });

  // The backend refuses session revocation while impersonating, for either profile.
  const mayForceLogout = !auth.isImpersonating && (isOwnProfile || permissions.has('can_edit_all_profiles'));
  const sessions = sessionsQuery.data ?? [];
  const actionLabel = isOwnProfile
    ? t('healthcareProviders.sessions.logOutEverywhere')
    : t('healthcareProviders.sessions.forceLogout');

  return (
    <Panel>
      <h3>{t('healthcareProviders.sessions.title')}</h3>
      {sessionsQuery.isLoading && <Spinner label={t('global.loading')} />}
      {sessionsQuery.isError && <Alert type="error">{t('healthcareProviders.sessions.loadError')}</Alert>}
      {!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length === 0 && (
        <p>{t('healthcareProviders.sessions.empty')}</p>
      )}
      {sessions.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeadCol>{t('healthcareProviders.sessions.client')}</Table.HeadCol>
              <Table.HeadCol>{t('healthcareProviders.sessions.lastActivity')}</Table.HeadCol>
              <Table.HeadCol>{t('healthcareProviders.sessions.ipAddress')}</Table.HeadCol>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {sessions.map((session, index) => (
              <Table.Row key={`${session.clientType}-${session.lastUsed}-${index}`}>
                <Table.Col>
                  {clientTypeLabel(session.clientType)}
                  {session.clientInfo && <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{session.clientInfo}</div>}
                </Table.Col>
                <Table.Col>{formatMoment(session.lastUsed)}</Table.Col>
                <Table.Col>{session.lastIp ?? '–'}</Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      {mayForceLogout && sessions.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Button buttonType="error" type="button" onClick={() => setConfirming(true)}>
            {actionLabel}
          </Button>
        </div>
      )}
      {confirming && (
        <Modal style={{ maxWidth: 450 }}>
          <h3 className="text-center mb-10">{actionLabel}</h3>
          <p className="text-center">
            {isOwnProfile
              ? t('healthcareProviders.sessions.logOutEverywhereWarning')
              : t('healthcareProviders.sessions.forceLogoutWarning')}
          </p>
          {revoke.isError && (
            <Alert type="error">
              {isOwnProfile
                ? t('healthcareProviders.sessions.logOutEverywhereError')
                : t('healthcareProviders.sessions.forceLogoutError')}
            </Alert>
          )}
          <Button
            className="mb-15 error"
            isBlock
            loading={revoke.isPending}
            buttonType="error"
            type="button"
            onClick={() => revoke.mutate()}
          >
            {actionLabel}
          </Button>
          <Button onClick={() => setConfirming(false)} isBlock buttonType="link">
            {t('global.cancel')}
          </Button>
        </Modal>
      )}
    </Panel>
  );
};
